<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            @rowSelected="preview"
            download-filename="Wideband Orders"
            ajax-path="wideband/orders"
            :ajax-params="ajaxParams"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"           
            :refresh.sync="refreshTable"
            :showHeader="showTableHeader"
            :pagination="paginateTable"
        />
        <app-modal 
            width="lg:w-2/3"
            bg="bg-gray-200"
            :show.sync="showPreviewModal"
            @close="showPreviewModal = false"
        >
            <template slot="header">ORDER DETAILS <small class="text-gray-600 uppercase">(STATUS: {{ order.status }})</small></template>
            <template slot="body">
                <div class="card">
                    <table class="table">
                        <tr>
                            <td><strong>Wideband Order ID</strong></td>
                            <td>
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.id }}</div>
                            </td>
                            <td><strong>Your Reference</strong></td>
                            <td>
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div class="break-all whitespace-normal" v-else>{{ order.access_seeker_ref || 'N/A' }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Created At</strong></td>
                            <td>
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.created_at_for_human }}</div>
                            </td>
                            <td><strong>Updated At</strong></td>
                            <td>
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.updated_at_for_human }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Access Technology</strong></td>
                            <td>
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.nbn_access_technology }}</div>
                            </td>
                            <td><strong>Order Type</strong></td>
                            <td>
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.order_type }}</div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>Address</strong></td>
                            <td colspan="3">
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.formatted_address }}<br>
                                <small class="text-gray-500">NBN Location ID: {{ order.nbn_location_id }}</small></div>
                            </td>
                        </tr>
                        <tr>
                            <td><strong>NBN Order ID</strong></td>
                            <td>
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.nbn_order_id }}</div>
                            </td>
                            <td v-if="order.nbn_pri_id"><strong>NBN PRI ID</strong></td>
                            <td v-if="order.nbn_pri_id">
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.nbn_pri_id }}</div>
                            </td>
                        </tr>
                        <tr v-if="order.nbn_avc_id">
                            <td><strong>NBN AVC ID</strong></td>
                            <td colspan="3">
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.nbn_avc_id }}</div>
                            </td>
                        </tr>
                        <tr v-if="order.appointment">
                            <td><strong>Appointment ID</strong></td>
                            <td>
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.appointment_id }}</div>
                            </td>
                            <td><strong>Appointment Status</strong></td>
                            <td>
                                <div class="whitespace-normal">
                                    {{ order.appointment.status }} 
                                    <span v-if="order.appointment.reason_code">({{ order.appointment.reason_code }})</span>
                                </div>
                            </td>
                        </tr>
                        <tr v-if="order.appointment">
                            <td width="210"><strong>Appointment Timeframe</strong></td>
                            <td colspan="3">
                                <div v-if="previewModalLoading" class="row-loading"></div>
                                <div v-else>{{ order.appointment.start_time_local }} - {{ order.appointment.end_time_local }}</div>
                            </td>
                        </tr>
                    </table>
                    <div v-if="order.appointment" class="py-5 text-center">
                        <app-button 
                            class="btn-blue"
                            @click="() => {
                                appointmentActionType = 'reschedule';
                                showAppointmentAvailabilityModal = true;
                                availableAppointmentForm.set({
                                    demandType: order.appointment.demand_type,
                                    nbnLocationId: order.nbn_location_id
                                })
                            }"
                        >Reschedule Appointment</app-button>
                    </div>
                    <div 
                        v-if="order.last_notification_type === 'AppointmentRequired'"
                        class="py-5 text-center"
                    >
                        <app-button 
                            class="btn-blue"
                            @click="() => {
                                appointmentActionType = 'reserve';
                                showAppointmentAvailabilityModal = true;
                                availableAppointmentForm.set({
                                    nbnLocationId: order.nbn_location_id
                                })
                            }"
                        >Reserve Appointment</app-button>
                    </div>
                </div>
                <h2 class="font-thin mt-10 mb-6 tracking-widest">ORDER UPDATES</h2>
                <div class="card">
                    <table class="table">
                        <thead>
                            <tr>
                                <th width="200">CREATED</th>
                                <th>NOTIFICATION TYPE</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="previewModalLoading">
                                <tr v-for="n in 3">
                                    <td v-for="n in 2">
                                        <div class="row-loading"></div>
                                    </td>
                                </tr>
                            </template>
                            <template v-else>
                                <tr v-for="update in order.updates">
                                    <td>{{ update.created_at_for_human }}</td>
                                    <td>
                                        <div>{{ update.notification_type }}</div>
                                        <div 
                                            class="text-xs text-gray-600 whitespace-normal"
                                            v-if="update.update_data && update.update_data.ProductOrder && update.update_data.ProductOrder.Note && update.update_data.ProductOrder.Note.DescribedBy"
                                        >
                                            Reason: {{ update.update_data.ProductOrder.Note.description }}<br>
                                            {{ update.update_data.ProductOrder.Note.DescribedBy.value }}
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showAppointmentAvailabilityModal"
            @close="() => {
                availableAppointmentForm.reset();
            }"
        >
            <template slot="header">Check Appointment Availability</template>
            <template slot="body">
                <app-select 
                    v-model="availableAppointmentForm.demandType"
                    placeholder="Demand Type"
                    :options="['Standard Install', 'Non EU Jumper Only', 'Service Restoration']"
                    :error="availableAppointmentForm.errors.get('demandType')"
                    @updated="availableAppointmentForm.errors.clear('demandType')"
                    required
                />
                <app-select 
                    v-model="availableAppointmentForm.slotType"
                    placeholder="Slot Type"
                    :options="['AM', 'PM']"
                    :error="availableAppointmentForm.errors.get('slotType')"
                    @updated="availableAppointmentForm.errors.clear('slotType')"
                    required
                />                
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showAppointmentAvailabilityModal = false"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-2"
                        :loading="checkingAppointmentAvailability"
                        @click="checkAvailableAppointment"
                    >Check</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showAvailableAppointmentsModal"
        >
            <template slot="header">Available Appointment Schedules</template>
            <template slot="body">
                <div
                    v-for="(a, key) in appointments"
                    :key="key"
                    class="px-3 py-2 flex items-center justify-between mb-2 rounded border"
                >
                    <div>{{ a.appointmentFormatted }}</div>
                    <app-button
                        class="btn-blue"
                        :disabled="reschedulingAppointment"
                        :loading="reschedulingAppointment && appointment.id == a.id"
                        @click="selectAppointment(a)"
                    >Select</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal 
            width="md:w-1/2 lg:w-1/3"
            :show.sync="showReserveAppointmentModal"
            @close="() => {
                reserveAppointmentForm.reset();
                reserveAppointmentForm.errors.clear();
            }"
        >
            <template slot="header">Reserve Appointment</template>
            <template slot="body">
                <app-input
                    v-model="appointment.appointmentFormatted"
                    placeholder="Date"
                    disable
                />
                <app-input
                    v-model="reserveAppointmentForm.contactName"
                    placeholder="Contact Name"
                    :error="reserveAppointmentForm.errors.get('contactName')"
                    @updated="reserveAppointmentForm.errors.clear('contactName')"
                    required
                    focus
                />
                <app-input
                    v-model="reserveAppointmentForm.contactPhone"
                    placeholder="Contact Phone"
                    :error="reserveAppointmentForm.errors.get('contactPhone')"
                    @updated="reserveAppointmentForm.errors.clear('contactPhone')"
                    required
                />
                <div class="flex items-center justify-end mt-5">
                    <app-button 
                        class="btn-white"
                        @click="showReserveAppointmentModal = false"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-2"
                        :loading="reservingAppointment"
                        @click="reserveAppointment"
                    >Reserve</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import Form from '@/helpers/Form';
    
    export default {
        name: 'widebandOutstandingOrders',
        props: {
            showTableHeader: {
                type: Boolean,
                default: true,
            },
            paginateTable: {
                type: Boolean,
                default: true,
            }
        },
        data() {
            return {
                showPreviewModal: false,
                previewModalLoading: false,
                checkingAppointmentAvailability: false,
                showAppointmentAvailabilityModal: false,
                showAvailableAppointmentsModal: false,
                showReserveAppointmentModal: false,
                reservingAppointment: false,
                reschedulingAppointment: false,
                tableIsLoading: false,
                tableLoadingText: null,
                appointmentActionType: null,
                ajaxParams: {type: 'outstanding'},
                refreshTable: false,
                availableAppointmentForm: new Form({
                    nbnLocationId: null,
                    demandType: null,
                    slotType: null,
                }),
                reserveAppointmentForm: new Form({
                    orderId: null,
                    nbnLocationId: null,
                    contactPhone: null,
                    contactName: null,
                    demandType: null,
                    timeSlot: null,
                    startTimeRaw: null,
                    endTimeRaw: null,
                }),
                columns: [
                    {
                        title: 'ID',
                        field: 'id',
                        sorter: 'string',
                        minWidth: 90,
                    },
                    {
                        title: 'Reference',
                        field: 'access_seeker_ref',
                        sorter: 'string',
                        minWidth: 220,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return _cell.value;
                            }

                            return 'N/A';
                        }
                    },
                    {
                        title: 'Created',
                        field: 'created_at_for_human',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        sorter: 'string',
                        minWidth: 150,
                    },
                    {
                        title: 'Last Notification',
                        field: 'last_notification_type',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Appoinment Timeframe',
                        field: 'appointment',
                        sorter: 'string',
                        minWidth: 350,
                        variableHeight: true,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return `${_cell.value.start_time_local} - ${_cell.value.end_time_local}`;
                            }

                            return 'N/A';
                        }
                    },
                    {
                        title: 'Appointment Status',
                        field: 'appointment',
                        sorter: 'string',
                        formatter({_cell}) {
                            if (_cell.value && _cell.value) {
                                return _cell.value.status;
                            }

                            return 'N/A';
                        },
                        minWidth: 200,
                    },
                    {
                        title: 'NBN Order ID',
                        variableHeight: true,
                        field: 'nbn_order_id',
                        minWidth: 180,
                    },
                    {
                        title: 'NBN Location ID',
                        variableHeight: true,
                        field: 'nbn_location_id',
                        minWidth: 200,
                    },
                    {
                        title: 'NBN AVC ID',
                        variableHeight: true,
                        field: 'nbn_avc_id',
                        minWidth: 180,
                    },
                    {
                        title: 'Last Updated',
                        field: 'updated_at_for_human',
                        sorter: 'string',
                        minWidth: 200,
                    }
                ],
                actions: [
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync
                    }
                ],
            }
        },
        computed: {
            ...mapGetters({
                orders: 'wideband/orders/items',
                order: 'wideband/orders/selected',
                appointments: 'wideband/appointments/items',
                appointment: 'wideband/appointments/selected',
            })
        },
        methods: {            
            preview(row) {
                this.showPreviewModal = true;
                this.$store.commit('wideband/orders/setSelected', row.data);
            },
            checkAvailableAppointment() {
                this.checkingAppointmentAvailability = true;
                this.$store.dispatch('wideband/appointments/check', this.availableAppointmentForm.payload())
                    .then(() => {
                        this.checkingAppointmentAvailability = false;
                        this.showAvailableAppointmentsModal = true;
                    })
                    .catch(({ response }) => {
                        this.checkingAppointmentAvailability = false;
                        this.availableAppointmentForm.errors.record(response.data);
                    })
            },
            selectAppointment(appointment) {
                this.$store.commit('wideband/appointments/setSelected', appointment);

                if (this.appointmentActionType === 'reschedule') {
                    return this.rescheduleAppointment(appointment);
                }

                if (this.appointmentActionType === 'reserve') {
                    this.reserveAppointmentForm.set(appointment);
                    this.reserveAppointmentForm.set({
                        orderId: this.order.nbn_order_id,
                        demandType: this.availableAppointmentForm.demandType,
                        nbnLocationId: this.availableAppointmentForm.nbnLocationId,
                    });
                    this.showReserveAppointmentModal = true;
                    return;
                }
            },
            rescheduleAppointment() {                
                this.reschedulingAppointment = true;
                this.$store.dispatch('wideband/appointments/reschedule')
                    .then(() => {
                        this.reschedulingAppointment = false;
                        this.showAvailableAppointmentsModal = false;
                        this.showAppointmentAvailabilityModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Appointment was successfully rescheduled.'
                        })
                    })
                    .catch(() => {
                        this.reschedulingAppointment = false;
                    })
            },
            reserveAppointment() {
                this.reservingAppointment = true;
                this.$store.dispatch('wideband/appointments/reserve', this.reserveAppointmentForm.payload())
                    .then(() => {
                        this.reservingAppointment = false;
                        this.showReserveAppointmentModal = false;
                        this.showAvailableAppointmentsModal = false;
                        this.showAppointmentAvailabilityModal = false;
                        this.showPreviewModal = false;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Appointment was successfully reserved.'
                        })
                    })
                    .catch(({ response }) => {
                        this.reservingAppointment = false;
                        this.reserveAppointmentForm.errors.record(response.data);
                    })
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                this.$store.dispatch('wideband/orders/sync', 'outstanding')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.'
                        });
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            }
        }
    }
</script>